import { useCallback } from 'react';

import { useParams } from '../../../../../contexts/RouterContext';
import { ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX } from '../../../../../hooks/api/issues/useIssueCategorySummary';
import { useCurrentRevision } from '../../../../../hooks/api/revisions/useCurrentRevision';
import { getQueryKey as getRevisionQueryKey } from '../../../../../hooks/api/revisions/useRevision';
import { getRevisionScansQueryKeyPrefix } from '../../../../../hooks/api/revisions/useRevisionScans';
import { queryClient } from '../../../../../ReactQuery';
import TaskModule from '../../../../../redux/tasks';
import useBuildList from '../../../ProjectBuildsPage/useBuildList';
import type { BuildWithRevision } from '../../../../../types';
import type { RevisionRouteParams } from '../../../projectDashboardTypes';

const { BuildSteps } = TaskModule.constants;

type RevisionBuildStatus = {
  isBuilding: boolean;
  isLoading: boolean;
  builds: BuildWithRevision[];
  onLatestComplete: () => void;
};

/**
 * Drives the building-vs-summary decision for Project Summary V2 off the
 * revision's server-computed `isSteady`. `useCurrentRevision` hits the full
 * endpoint (which computes `isSteady`) while the revision is building and the
 * lighter minimal endpoint once it is steady, so `isSteady` is reliable here.
 *
 * A terminally failed build makes the revision steady, so we also keep showing
 * the build UI when the latest build has failed (its red FAILED badge), matching
 * V1. The build list is fetched to render the stepper card(s) and to fire
 * `onLatestComplete` when the latest build finishes.
 */
export const useRevisionBuildStatus = (): RevisionBuildStatus => {
  const params = useParams<RevisionRouteParams>();
  const locator = `${params.project_id}$${params.revision_id}`;

  const { isSteady, isLoading: isRevisionLoading } = useCurrentRevision();
  const { builds, buildStatus } = useBuildList({ mode: 'REVISION', locator });

  const isLatestBuildFailed = TaskModule.fns.getBuildStep(builds[0]?.task) === BuildSteps.Failed;

  // Once the latest build finishes, refresh the revision, scans, and issue
  // category summary caches so the summary swaps in with fresh data. Mirrors
  // the invalidation set in `useScanRevision`.
  const onLatestComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getRevisionQueryKey(locator) });
    queryClient.invalidateQueries({ queryKey: getRevisionScansQueryKeyPrefix(locator) });
    queryClient.invalidateQueries({ queryKey: ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX });
  }, [locator]);

  return {
    isBuilding: !isSteady || isLatestBuildFailed,
    isLoading: isRevisionLoading || buildStatus.isLoading,
    builds,
    onLatestComplete,
  };
};
